// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, useMemo, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Search, MessageSquare, Bell, AlertCircle } from 'lucide-react'
import * as api from '../../api/client'
import { listFacilityRx, getFacilityRxFacet } from '../../api/siteOfCare'
import { useDrugRefValues } from '../../hooks/useDrugRefValues'
import CategoryBadge from '../../components/common/CategoryBadge'
import { friendlyError } from '../../api/errors'
import { useServerPaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { ListTable, toServerFilters, type ListTableCap } from '../../components/common/ListTable'
import type { ColumnFilters } from '../../components/common/ColumnFilter'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import ProviderHome from './ProviderHome'
import {
  PayerStatePill,
  ErrorBanner, LoadingState, EmptyState, relativeTime,
} from '../../components/workflow/StatePills'
import { ShipStatusPill } from '../../components/workflow/ShipStatusPill'
import { useAuth } from '../../auth/AuthContext'
import PendingSignaturesCard from '../../components/carerx/PendingSignaturesCard'
import { formatDob } from '../../api/format'

interface Props {
  onScreenEnter: () => void
  /** D-SOC-9 S2/S3 — which portal is mounting this. The component is identical for both; only
   *  the DATA SOURCE and a few labels differ, so a facility gets the SAME three working tabs,
   *  the same column customization and the same filters rather than a stripped-down copy.
   *  Forking this file would duplicate the column-manager wiring, which is the shape L-067
   *  warns about: share the unit that gets mounted. */
  portal?: 'provider' | 'facility'
}

// The funnel asks the same question the Delivery column answers: where is my patient's
// medication? It used to filter the PMS clinical state (Submitted / Verified / Ready to fill /
// Dispensed), which describes the pharmacy's internal workflow and left a prescriber guessing
// which of those meant "on its way". Buckets, not raw carrier statuses — 'created' vs
// 'label_purchased' is not a distinction a prescriber makes. The server applies them over the
// whole scope (?ship=), so a bucket is never "…among the rows this page happened to load".
// D-SOC-2 (S2): the seeded site-of-care codes in plain words. A prescriber must never read
// `infusion_center` on their own screen (P3: no engine names or codes in the UI). Falls back to
// the raw code for an admin-added type — wrong-looking beats blank, and it self-reports that a
// pharmacy added a type this map has not learned. The pharmacy-side label lives in
// dbo.site_of_care_types; duplicating the whole registry into the provider bundle would mean a
// second fetch on every portal load for six strings that change about once a year.
export const SITE_OF_CARE_LABELS: Record<string, string> = {
  home: 'Home',
  infusion_center: 'Infusion center',
  ais: 'Infusion suite',
  clinic_site: 'Clinic / physician office',
  snf: 'Nursing home',
  board_care: 'Board & care',
  self_admin: 'Self-administered',
}

const SHIP_FILTERS: { value: string; label: string }[] = [
  { value: '',                  label: 'All' },
  { value: 'not_shipped',       label: 'Not shipped' },
  { value: 'in_transit',        label: 'In transit' },
  { value: 'out_for_delivery',  label: 'Out for delivery' },
  { value: 'delivered',         label: 'Delivered' },
  { value: 'exception',         label: 'Delivery issue' },
]

// D-SOC-9 — adapt the facility Rx page to the provider row shape so ONE table component serves
// both portals. Fields the facility endpoint does not project are left NULL rather than faked:
// the table renders an em dash, and the pharmacy adds the column it wants from the SaaS admin
// builder (fac_rx_list), where PioneerRx-mapped values arrive through batchCustomFieldValues
// exactly as they do for the provider surface.
async function facilityRxAsProviderPage(o: any) {
  // D-SOC-10 B-2: sort and filter are FORWARDED now. They used to be dropped here silently,
  // while FACILITY_COLUMN_CAPS told the header those columns were sortable and filterable —
  // so every control was inert and the tooltip read "cannot be filtered on this list".
  const d = await listFacilityRx({
    page: o.page, page_size: o.pageSize, q: o.q, facility: o.facility,
    order_by: o.orderBy || o.order_by, order_dir: o.orderDir || o.order_dir,
    // The table hands over `filters` (key, op, values); the endpoint reads `filter=key:op:v1,v2`.
    // This line used to pass `o.filter`, a name nothing ever set, so a funnel on the facility
    // prescriptions table drew its chip and the request went out unfiltered (found 2026-09-29 by
    // the lab spec that watches the request, not the chip).
    filter: o.filter ?? api.encodeRxFilters(o.filters ?? []),
  })
  return {
    ...d,
    items: (d.items ?? []).map((r): any => ({
      id: r.rx_id,
      rx_number: r.rx_number,
      drug_name: r.drug ?? '',
      drug_strength: r.strength ?? '',
      quantity_authorized: r.quantity ?? 0,
      days_supply: r.days_supply,
      state: r.state ?? '',
      patient_name: r.patient ?? '',
      prescriber_name: r.prescriber ?? '',
      last_state_change_at: r.last_activity_at ?? '',
      site_of_care_facility: r.facility_name,
      // Values of the pharmacy's configured custom columns, projected by facility/rx inside the
      // facility scope (keys "list_columns.<column>", the same keys the batch endpoint uses).
      custom: (r as any).custom ?? null,
      ship_status: r.ship_status,
      ship_tracking: r.ship_tracking,
      ship_delivered_at: r.ship_delivered_at,
    })),
  }
}

// D-SOC-10 B-2 — what the facility Rx list can actually sort and filter on. Every key here is a
// column facility/rx projects; a key that is not projected would make the header offer a control
// that silently does nothing, which is the defect class this whole document is about.
// These MUST match facility/rx's sortMap and filterMap exactly. When this list said a column was
// sortable the endpoint had a FIXED ORDER BY and accepted no filter at all, so every control was
// inert — the map described a server that did not exist. Both maps are now real; keep the three
// in step (facilityColumnCaps.test.ts fails the build if they drift).
const FACILITY_COLUMN_CAPS = [
  // `facetable` and `klass` are NOT optional decoration. ListTable line ~211 reads
  //   if (!cap?.facetable && !rangeMode) return <disabled marker/>
  // so a column with filterable:true but no facetable renders NO FUNNEL AT ALL — which is
  // exactly what Mario verified on ASP after the last deploy: "there no col filters like
  // pharmacy tables at all". The provider registry emits both (PatientColumnRegistry.Describe:
  // klass = def.Class..., facetable = def.Facetable), and this hand-written map did not. A
  // second list of capabilities is how this drifts; the real fix is a facility/rx/columns
  // endpoint, which stays tracked.
  //
  // klass drives the control: 'enum' = a ticklist fed by the facet endpoint, 'date'/'numeric' =
  // from/to bounds with no facet call, 'refused' + ops contains = a text box (D-UI-2).
  //
  // Patrick, 2026-09-29: "every single col to have the funnel and be filterable, no exceptions".
  // The resident's name has its funnel now. It is a CONTAINS box, not a value list: the server
  // matches what was typed and never enumerates the roster, so the privacy decision that kept
  // this column without a control (a name list discloses who lives there) still holds.
  { key: 'patient_name', sortable: true, filterable: true, facetable: false, klass: 'refused' as const, ops: ['contains'],
    reason: 'Resident names are searched, never listed. Type part of a name.' },
  // Patrick, 2026-09-28: "the funnel for filters must be working in any table in the facility
  // portal". Every column below filters on the server. Text columns list their values (the
  // popover's search box reaches past the cap); numbers and dates take bounds.
  { key: 'rx_number', sortable: true, filterable: true, facetable: true, klass: 'enum' as const },
  { key: 'drug_name', sortable: true, filterable: true, facetable: true, klass: 'enum' as const },
  { key: 'drug_strength', sortable: true, filterable: true, facetable: true, klass: 'enum' as const },
  { key: 'prescriber_name', sortable: true, filterable: true, facetable: true, klass: 'enum' as const },
  { key: 'quantity_authorized', sortable: true, filterable: true, facetable: false, klass: 'numeric' as const },
  { key: 'days_supply', sortable: true, filterable: true, facetable: false, klass: 'numeric' as const },
  { key: 'state', sortable: true, filterable: true, facetable: true, klass: 'enum' as const },
  { key: 'ship_status', sortable: true, filterable: true, facetable: true, klass: 'enum' as const },
  { key: 'ship_tracking', sortable: true, filterable: true, facetable: true, klass: 'enum' as const },
  { key: 'facility_name', sortable: true, filterable: true, facetable: true, klass: 'enum' as const },
  // 2026-09-28: a date RANGE, no facet — the funnel draws From/To and the server takes gte/lte.
  { key: 'last_activity', sortable: true, filterable: true, facetable: false, klass: 'date' as const },
]


// Capabilities for the facility PATIENTS table. Must match facility/patients' patSortMap and
// patFilterMap exactly — facilityColumnCaps.test.ts fails the build if they drift.
//
// Only Facility is facetable: it binds soc.facility_id, which is already the scope predicate and
// is indexed. Resident and MRN filter by CONTAINS (D-UI-2): a text box, never a list, because
// enumerating names or MRNs in a dropdown would disclose the roster.
const FACILITY_PATIENT_CAPS = [
  { key: 'patient_name', sortable: true, filterable: true, facetable: false, klass: 'refused' as const, ops: ['contains'],
    reason: 'Resident names are searched, never listed. Type part of a name.' },
  // 2026-09-28 (Patrick, facility walk-through): ranges are real filters now — the server takes
  // gte/lte in HAVING for these two aggregates, so the funnel's From/To and Min/Max narrow the list.
  { key: 'rx_count', sortable: true, filterable: true, facetable: false, klass: 'numeric' as const },
  { key: 'date_of_birth', sortable: true, filterable: true, facetable: false, klass: 'date' as const },
  { key: 'mrn', sortable: true, filterable: true, facetable: false, klass: 'refused' as const, ops: ['contains'],
    reason: 'MRNs are searched, never listed. Type part of an MRN.' },
  // `key` is the SERVER's name (patSortMap / patFilterMap); `col` is the TABLE's column key where
  // the two differ. ListTable looks a capability up by the column key, so without `col` these two
  // never matched: Facility and Last activity rendered the "filter unavailable" marker and ignored
  // header clicks in production (ASP 2026-09-28 15:52, reproduced in the lab). The column keys
  // are persisted (view-builder config, width store), so the table keeps its names and the map
  // translates.
  { key: 'last_activity', col: 'last_rx_at', sortable: true, filterable: true, facetable: false, klass: 'date' as const },
  { key: 'facility_name', col: 'facility_names', sortable: false, filterable: true, facetable: true, klass: 'enum' as const },
]
const patColKey = (c: { key: string; col?: string }) => c.col ?? c.key
const FACILITY_PATIENT_CAPS_MAP: Record<string, ListTableCap> =
  Object.fromEntries(FACILITY_PATIENT_CAPS.map(c => [patColKey(c), c as ListTableCap]))
/** Table column key -> the name the facility/patients endpoint knows it by. */
const FACILITY_PATIENT_SERVER_KEY: Record<string, string> =
  Object.fromEntries(FACILITY_PATIENT_CAPS.map(c => [patColKey(c), c.key]))
const patServerKey = (colKey: string) => FACILITY_PATIENT_SERVER_KEY[colKey] ?? colKey
// D-UI-2: My patients in the PROVIDER portal sorts and filters on the same keys, answered by
// provider/patients (provPatSortMap / provPatFilterMap; providerColumnCaps.test.ts holds the two
// in step). A prescriber has no facility grant, so there is no Facility column to filter.
const PROVIDER_PATIENT_CAPS_MAP: Record<string, ListTableCap> =
  Object.fromEntries(FACILITY_PATIENT_CAPS.filter(c => c.key !== 'facility_name')
    .map(c => [patColKey(c), { ...c, reason: c.reason?.replace('Resident names', 'Patient names') } as ListTableCap]))

// Delivery and Site of care store codes; the cell shows words, so the funnel does too.
const SOC_FUNNEL_KEYS = new Set(['site_of_care', 'site_of_care_type'])
const words = (code: string) => { const s = code.replace(/_/g, ' '); return s.charAt(0).toUpperCase() + s.slice(1) }

export default function ProviderDashboard({ onScreenEnter, portal = 'provider' }: Props) {
  // The whole of the facility variant is these five calls plus the view-builder surface below.
  const isFac = portal === 'facility'
  const DS = {
    summary: (): Promise<api.ProviderSummary> => (isFac ? api.getFacilitySummary() : api.getProviderSummary()),
    listRx: (o: any): Promise<any> => (isFac
      ? facilityRxAsProviderPage(o).then(d => { const dn = ((d as any).denied ?? []) as string[]; setFacRxDenied(p => sameList(p, dn) ? p : dn); return d })
      : api.listProviderRx(o)),
    listPatients: (o: any): Promise<any> => (isFac
      ? api.listFacilityPatients(o).then(d => { const dn = ((d as any).denied ?? []) as string[]; setFacPtDenied(p => sameList(p, dn) ? p : dn); return d })
      : api.listProviderPatients(o).then(d => { const dn = ((d as any).denied ?? []) as string[]; setFacPtDenied(p => sameList(p, dn) ? p : dn); return d })),
    // Column caps and facets are Rx-list query features the facility endpoint does not implement
    // yet. Degrade to "no caps / no facet options" rather than calling a provider endpoint a
    // facility role would be refused on — that 403 is exactly what D-SOC-9 removed.
    // D-SOC-10 B-2 (Patrick 2026-09-25): "Not able to sort or filter on the Facility column".
    // This used to return { items: [] }, which the header reads as "nothing is sortable" — so
    // EVERY column in the facility portal was inert, not just Facility. That was my stub, not a
    // product decision.
    //
    // Static, and truthful about exactly what facility/rx can order by: these are the columns it
    // projects. Priya's dissent stands (D-SOC-10 §5) — a static map drifts the moment the
    // projection changes, which is the same catalog-vs-consumer drift as B-4. The real fix is a
    // facility/rx/columns endpoint; Nadia carries it.
    caps: (): Promise<any> => (isFac
      ? Promise.resolve({ items: FACILITY_COLUMN_CAPS } as any)
      : api.getProviderRxColumnCaps()),
    // D-SOC-10 B-2 (Mario 2026-09-27): this was a hardcoded empty stub, so every filterable
    // column offered an EMPTY picker — which the UI correctly renders as "cannot be filtered".
    // Caps without facets is not a half-fix; it advertises a control that cannot work.
    facet: (o: any): Promise<any> => (isFac
      ? getFacilityRxFacet({ column: o.column ?? o.key, q: o.q, facility: o.facility })
      : api.getProviderRxFacet(o)),
  }
  const navigate = useNavigate()
  const { me, role } = useAuth()

  // ── Query state and the two paged lists ───────────────────────────────────────────────────
  // Declared FIRST because the column config below reads `items` during render (useDrugRefValues),
  // and `const items` must therefore be initialized before that line runs.
  // My Patients uses the SAME table. Columns are declared rather than inlined in the markup so
  // the Columns manager and the width store have stable keys to persist against — an inline <th>
  // has no key to remember a width by.
  // D-SOC-9: the patients table is configurable on the FACILITY surface (fac_pt_list), the same
  // way the Rx table is. Mario, 2026-09-25: "the patients table can be customized like every
  // other view". The provider surface has no catalog yet, so it keeps the fixed set below —
  // adding one later is a catalog entry plus (if the column is new) a unit here, and
  // columnRendererParity.test.ts fails the build if those two drift apart.
  const [ptCfg, setPtCfg] = useState<api.RxViewLayout | null>(null)
  // RCA-CV-5 L7 (E-07): every layout read on this page used to swallow its failure and render
  // the default columns, so "the portal shows defaults" was indistinguishable from "nothing was
  // configured" — the shape of dab2422e and 898a614a, diagnosed over days. Defaults still
  // render; the page now says why.
  const [layoutIssue, setLayoutIssue] = useState<string | null>(null)
  const layoutFailed = (what: string) => () =>
    setLayoutIssue(`The configured ${what} could not be loaded; showing the default columns.`)
  useEffect(() => {
    if (!isFac) { setPtCfg(null); return }
    api.getRxViewLayout(undefined, 'fac_pt_list').then(setPtCfg).catch(layoutFailed('patients columns'))
  }, [isFac])
  const ptCfgCard = useMemo(() => (((ptCfg?.cards as any[] | undefined) ?? []).find(c => c.card_key === 'list_columns')), [ptCfg])

  // D-SOC-9: the facility HOME tab's cards, configured from the SaaS admin (fac_home). A card
  // surface, not a column surface — the sections ARE the cards, so a visible card row means
  // "show this tile". null = never configured = show the defaults.
  const [homeCfg, setHomeCfg] = useState<api.RxViewLayout | null>(null)
  useEffect(() => {
    if (!isFac) { setHomeCfg(null); return }
    api.getRxViewLayout(undefined, 'fac_home').then(setHomeCfg).catch(layoutFailed('home tiles'))
  }, [isFac])
  const homeSections = useMemo(() => {
    const cs = (homeCfg?.cards as any[] | undefined) ?? []
    if (homeCfg?.source !== 'custom' || cs.length === 0) return null
    return new Set(cs.filter(c => c.visible).map(c => String(c.card_key)))
  }, [homeCfg])

  const patientCols = useMemo(() => {
    const units = [
      { key: 'patient_name', label: 'Patient',
        render: (p: api.ProviderPatientItem) => <>{p.display_name || '—'}</> },
      { key: 'mrn', label: 'MRN',
        render: (p: api.ProviderPatientItem) => <>{p.mrn || '—'}</> },
      { key: 'date_of_birth', label: 'DOB',
        render: (p: api.ProviderPatientItem) =>
          <>{(p as any).date_of_birth ? formatDob((p as any).date_of_birth) : '—'}</> },
      { key: 'rx_count', label: 'Prescriptions',
        render: (p: api.ProviderPatientItem) => <>{p.rx_count}</> },
      { key: 'last_rx_at', label: 'Last activity',
        render: (p: api.ProviderPatientItem) => <>{p.last_rx_at ? relativeTime(p.last_rx_at) : '—'}</> },
      // D-SOC-10 B-3: the facilities this resident's in-scope Rx resolve to, de-duplicated
      // server-side. Facility-only: a provider has no facility grant to render.
      { key: 'facility_names', label: 'Facility',
        render: (p: api.ProviderPatientItem) => <>{(p as any).facility_names || '—'}</> },
    ]
    // The historical provider set, unchanged: display_name is the same column as patient_name.
    // D-SOC-10 B-3: Facility is a DEFAULT for the facility portal — Patrick asked for the column,
    // and a default that needs configuring first is the no-config blank-table bug again.
    const DEFAULTS = isFac
      ? ['patient_name', 'rx_count', 'facility_names', 'last_rx_at']
      : ['patient_name', 'date_of_birth', 'rx_count', 'last_rx_at']
    const byKey = new Map(units.map(u => [u.key, u]))
    const fields: any[] = ptCfgCard?.fields ?? []
    if (!fields.length) return DEFAULTS.map(k => byKey.get(k)!).filter(Boolean)
    const out = fields.filter(f => f.visible).map(f => {
      const u = byKey.get(f.owlrx_column)
      if (!u && import.meta.env.DEV)
        console.warn(`[view-builder] patients column "${f.owlrx_column}" is configured but has no renderer — it will not appear.`)
      return u ? { ...u, label: (f.owlrx_label || '').trim() || u.label } : null
    }).filter((u): u is typeof units[number] => !!u)
    return out.length ? out : DEFAULTS.map(k => byKey.get(k)!).filter(Boolean)
  }, [ptCfgCard, isFac])

  const [error, setError] = useState<string | null>(null)
  const [shipFilter, setShipFilter] = useState('')
  // D-SOC-6: filter the provider's list by site of care. The column is already shown (S2), so
  // this makes the list answer the question that column invites — "show me just the infusions".
  const [socFilter, setSocFilter] = useState('')
  // Types come from the DIRECTORY, not from a hardcoded list. dbo.site_of_care_types is data a
  // pharmacy edits (SiteOfCareTab); a literal map here silently omitted two active types and
  // would hide any type added after a deploy, which is precisely the drift the table exists to
  // prevent. Falls back to the label map below if the call fails, so the chips never vanish.
  const [socTypes, setSocTypes] = useState<{ code: string; label: string }[]>([])
  useEffect(() => {
    // D-SOC-9: skip for a facility. GET /api/site-of-care/types is a pharmacy-settings endpoint
    // that 403s for this role (observed in the browser 2026-09-25), and the chips it feeds are
    // meaningless here anyway — every row in a facility portal IS a facility. Calling it and
    // swallowing the 403 would still put a guaranteed-failing request in the console on every
    // mount, which is the noise that hid the real 403s earlier in this feature.
    if (isFac) return
    let dead = false
    api.listSiteOfCareTypes(true)
      .then(r => { if (!dead) setSocTypes((r.items ?? []).map(t => ({ code: t.code, label: t.label }))) })
      .catch(() => { /* fall back to the built-in labels */ })
    return () => { dead = true }
  }, [isFac])
  const [q, setQ] = useState('')
  const [patientQ, setPatientQ] = useState('')
  const [unreadCount, setUnreadCount] = useState(0)
  const location = useLocation()
  // Owner 2026-08-09: /provider is now a HOME dashboard; the lists live at /provider/prescriptions
  // and /provider/patients (home used to duplicate My prescriptions).
  // Derive from the path SUFFIX, never an exact match: this portal also renders under the branded
  // pharmacy route (/p/{url_code}/...), where the path is not '/provider' at all. Anything that is
  // not explicitly a list route is HOME.
  // D-SOC-9: '/residents' is the facility portal's patients route — the nav says "My patients"
  // but the URL kept the resident wording. Without it here every facility tab fell through to
  // HOME, which is what Mario saw: "there is no view for the patient view i click on it i get to
  // the home". Suffix, never exact match, because both portals also render under the branded
  // /p/{url_code}/... route.
  const provPath = location.pathname.replace(/\/+$/, '')
  const tab: 'home' | 'rx' | 'patients' =
    provPath.endsWith('/patients') || provPath.endsWith('/residents') ? 'patients'
      : provPath.endsWith('/prescriptions') || provPath.endsWith('/rx') ? 'rx'
      : 'home'

  // D-PROV-2 — both lists are SERVER-paged, server-filtered and server-searched.
  //
  // What was here before: fetch the newest 1,000 rows, then filter/search/page them in the browser.
  // Against ASP's 44k Rx and 10k+ patients that is not a display concern, it is a correctness one —
  // the search box answered from the loaded window, so a patient outside it produced "no matches"
  // rather than "not loaded". Typing more made the answer look more confident and no less wrong.
  //
  // Debounced so a keystroke is not a query. 2+ chars, matching SupportQueuePage: a single
  // character matches most of a 44k-row tenant and costs a full scan to tell you so.
  const debouncedQ = useDebouncedValue(q, 300)
  const debouncedPatientQ = useDebouncedValue(patientQ, 300)
  const serverQ = debouncedQ.trim().length >= 2 ? debouncedQ.trim() : ''
  const serverPatientQ = debouncedPatientQ.trim().length >= 2 ? debouncedPatientQ.trim() : ''

  // D-PROVGRID-1 WS2 — the provider list gets the pharmacy list's table, not a copy of it.
  //
  // Capabilities come from the SERVER registry (/provider/rx/columns). The portal keeps no second
  // list of what can be sorted or filtered: a second list is exactly how the pharmacy list ended
  // up with a 9-column sort whitelist facing ~40 header funnels, 31 of which quietly did something
  // other than what they appeared to do.
  const [caps, setCaps] = useState<Record<string, ListTableCap>>({})
  // The facility Rx table's saved columns, read here (as well as where the columns are built)
  // because the capability map below needs it before that point in the component.
  const [facCapsCard, setFacCapsCard] = useState<any>(null)
  const [facRxDenied, setFacRxDenied] = useState<string[]>([])
  const [facPtDenied, setFacPtDenied] = useState<string[]>([])
  const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i])
  useEffect(() => {
    if (!isFac) return
    let dead = false
    api.getRxViewLayout(undefined, 'fac_rx_list')
      .then(l => { if (!dead) setFacCapsCard((((l.cards as any[] | undefined) ?? []).find(c => c.card_key === 'list_columns')) ?? null) })
      .catch(() => { /* the column builder reports a layout failure; caps simply stay native */ })
    return () => { dead = true }
  }, [isFac])
  useEffect(() => {
    let dead = false
    DS.caps()
      .then(r => { if (!dead) setCaps(Object.fromEntries((r.items ?? []).map((c: any) => [c.key, c]))) })
      .catch(() => { /* no caps => every funnel reports itself unavailable, never a fake one */ })
    return () => { dead = true }
  }, [])

  // Columns this table RENDERS that the server registry does not own, each with the honest reason
  // and — where one exists — the control that does work. The generic "cannot be filtered here" the
  // fallback produced was true but useless: it did not say why, or what to use instead.
  const PORTAL_ONLY_REASONS: Record<string, string> = {
    ship_status: 'Delivery is filtered with the chips above the table. One control per concept, so the two can never disagree.',
    site_of_care: 'Where this patient receives the medication — from their CareRx therapy, not from the shipping address. Blank when the pharmacy has not recorded one.',
    comments: 'The comment count is computed per row, not stored as a column, so there is nothing to filter on.',
    patient_dob: 'Date of birth has no server-side filter yet. The search box above matches a DOB.',
  }
  const [colFilters, setColFilters] = useState<ColumnFilters>({})
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  // Entity funnels bind IDS so the predicate is exact; remember the labels the facet returned so
  // the chips and ticked boxes read as names rather than GUIDs.
  const [filterLabels, setFilterLabels] = useState<Record<string, Record<string, string>>>({})
  const rememberLabels = useCallback((key: string, pairs: { value: string; label: string }[]) => {
    setFilterLabels(m => {
      const forKey = { ...(m[key] ?? {}) }
      let changed = false
      for (const p of pairs) if (forKey[p.value] !== p.label) { forKey[p.value] = p.label; changed = true }
      return changed ? { ...m, [key]: forKey } : m
    })
  }, [])
  const labelForFilter = useCallback(
    (key: string, value: string) => filterLabels[key]?.[value], [filterLabels])

  // Server capabilities, plus an explicit entry for every portal-only column so its funnel can
  // state a real reason instead of falling through to the generic one.
  // FACILITY: a custom column the pharmacy added sorts and filters like any other. The server
  // resolves "cf:<column>" to the column's value (facility/rx, facility/rx/facet), so its
  // capability is known from the configuration alone and needs no second list to drift from.
  const facCustomCaps = useMemo(() => {
    const out: Record<string, ListTableCap> = {}
    if (!isFac) return out
    for (const f of (((facCapsCard?.fields ?? []) as any[])))
      if (f.visible && f.owlrx_table === 'custom' && /^[A-Za-z0-9_]+$/.test(f.owlrx_column ?? ''))
        out[`cf:${f.owlrx_column}`] = { sortable: true, filterable: true, facetable: true, klass: 'enum' } as ListTableCap
    return out
  }, [isFac, facCapsCard])
  // Keys the SERVER refuses for this account because the pharmacy masks the field for its role
  // (facility/rx and facility/patients report them as `denied`). A masked field is not filterable
  // or sortable — the surviving rows and their order would disclose what the mask hides — so the
  // header says that, instead of offering a control the server ignores.
  const MASKED_REASON = 'This field is masked for your role, so it cannot be filtered or sorted.'
  const maskCaps = (src: Record<string, ListTableCap>, denied: string[]) => {
    if (!denied.length) return src
    const out: Record<string, ListTableCap> = { ...src }
    for (const k of denied)
      out[k] = { ...(out[k] ?? {}), sortable: false, filterable: false, facetable: false, klass: 'refused', reason: MASKED_REASON } as ListTableCap
    return out
  }
  const capsAll = useMemo(() => maskCaps({ ...caps, ...facCustomCaps }, facRxDenied), [caps, facCustomCaps, facRxDenied])
  const patCaps = useMemo(() => maskCaps(isFac ? FACILITY_PATIENT_CAPS_MAP : PROVIDER_PATIENT_CAPS_MAP,
    facPtDenied.map(k => Object.entries(FACILITY_PATIENT_SERVER_KEY).find(([, server]) => server === k)?.[0] ?? k)), [facPtDenied, isFac])
  // Words for the codes a funnel lists: the site-of-care directory's own label where it has one.
  const funnelWords = useCallback((key: string, value: string): string | undefined => {
    if (SOC_FUNNEL_KEYS.has(key))
      return SITE_OF_CARE_LABELS[value] ?? socTypes.find(t => t.code === value)?.label ?? words(value)
    if (key === 'ship_status') return words(value)
    return undefined
  }, [socTypes])

  const effectiveCaps = useMemo(() => {
    const out: Record<string, ListTableCap> = { ...capsAll }
    for (const [k, reason] of Object.entries(PORTAL_ONLY_REASONS))
      if (!out[k]) out[k] = { sortable: false, filterable: false, facetable: false, reason }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capsAll])

  const { serverFilters, blockedCols } = useMemo(
    () => toServerFilters(colFilters, capsAll), [colFilters, capsAll])
  const filterSig = api.encodeRxFilters(serverFilters).join('|')

  const rxPaged = useServerPaged<api.ProviderRxItem>(
    ({ page, pageSize }) => DS.listRx({
      page, pageSize,
      ...(serverQ ? { q: serverQ } : {}),
      ...(shipFilter ? { ship: shipFilter } : {}),
      ...(socFilter ? { siteOfCare: socFilter } : {}),   // D-SOC-6
      ...(sortKey ? { orderBy: sortKey, orderDir: sortDir } : {}),
      ...(serverFilters.length ? { filters: serverFilters } : {}),
    }),
    DEFAULT_PAGE_SIZE,
    // filterSig, not the array: a new array identity every render would refetch on every render.
    [serverQ, shipFilter, socFilter, sortKey, sortDir, filterSig],
  )

  // Only fetched for the tabs that render it. `patientsWanted` is expressed as a dep rather than a
  // conditional hook call — hooks cannot be called conditionally.
  const patientsWanted = tab === 'patients' || tab === 'home'
  // D-SOC-10 (Mario, /facility/residents "patients have no col filters at all"): the patients
  // table got neither caps nor onFilters, so ListTable drew no funnels at all — a different gap
  // from the Rx table's, which had funnels that did nothing. Its own state, because the two
  // tables filter independently and sharing colFilters would cross-wire them.
  const [patColFilters, setPatColFilters] = useState<ColumnFilters>({})
  const [patSortKey, setPatSortKey] = useState<string | null>(null)
  const [patSortDir, setPatSortDir] = useState<'asc' | 'desc'>('desc')
  const patServerFilters = useMemo(
    () => toServerFilters(patColFilters, patCaps).serverFilters
      .map(f => ({ ...f, key: patServerKey(f.key) })), [patColFilters, patCaps])
  const patFilterSig = api.encodeRxFilters(patServerFilters).join('|')

  const patientsPaged = useServerPaged<api.ProviderPatientItem>(
    ({ page, pageSize }) => patientsWanted
      ? DS.listPatients({
          page, pageSize,
          ...(serverPatientQ ? { q: serverPatientQ } : {}),
          ...(patSortKey ? { orderBy: patServerKey(patSortKey), orderDir: patSortDir } : {}),
          ...(patServerFilters.length ? { filters: patServerFilters } : {}),
        })
      : Promise.resolve({ items: [], total: 0, page: 1, page_size: pageSize, total_pages: 0 }),
    DEFAULT_PAGE_SIZE,
    [serverPatientQ, patientsWanted, patFilterSig, patSortKey, patSortDir],
  )

  const items = rxPaged.rows
  const patients = patientsPaged.rows
  const loading = rxPaged.loading
  const patientsLoading = patientsPaged.loading

  // Practice-wide counts for the home tiles and the prior-auth banner. These CANNOT be derived from
  // `items` any more: that array is now one page, so counting it would answer "how many of these 25
  // rows need attention" while presenting itself as the practice total. The server computes them
  // over the whole scope.
  const [summary, setSummary] = useState<api.ProviderSummary | null>(null)
  useEffect(() => {
    let dead = false
    DS.summary()
      .then(s => { if (!dead) setSummary(s) })
      .catch(() => { if (!dead) setSummary(null) })
    return () => { dead = true }
  }, [])

  useEffect(() => {
    const e = rxPaged.error ?? (patientsWanted ? patientsPaged.error : null)
    setError(e ? friendlyError(e) : null)
  }, [rxPaged.error, patientsPaged.error, patientsWanted])

  // D-VIEWCOLS-4 ph2: values for PioneerRx/custom mapped columns (extracted at ingest).
  // One page's worth per fetch now, rather than a batch keyed on up to 1,000 ids.
  const [cfv, setCfv] = useState<Record<string, Record<string, string | null>>>({})
  useEffect(() => {
    if (items.length === 0) return
    // FACILITY: the values arrive WITH the rows (facility/rx projects them inside the facility
    // scope). The batch endpoint needs rx.read, which the Facility Staff role does not hold, so on
    // ASP this call was refused and — because the failure was swallowed below — every column a
    // pharmacy added to the facility table rendered a dash on every row, with nothing said.
    if (isFac) {
      setCfv(Object.fromEntries(items.map(i => [i.id, ((i as any).custom ?? {}) as Record<string, string | null>])))
      return
    }
    let dead = false
    api.batchCustomFieldValues('rx', items.map(i => i.id)).then(r => { if (!dead) setCfv(r.values ?? {}) })
      .catch(layoutFailed('custom column values'))
    return () => { dead = true }
  }, [items, isFac])

  // D-VIEWCOLS-3: the provider My Prescriptions table is driven by the pharmacy's
  // provider_rx_list config (order / visibility / labels / sub-text), managed from
  // the SaaS admin view builder. No config -> the default column set below.
  const [listCfg, setListCfg] = useState<api.RxViewLayout | null>(null)
  // D-SOC-9: the surface follows the PORTAL, so a facility's columns come from fac_rx_list and a
  // provider's from provider_rx_list. Everything downstream — order, visibility, labels, subtext,
  // PioneerRx-mapped columns via batchCustomFieldValues, drug-reference columns — is surface
  // agnostic and works unchanged. Mario, 2026-09-25: "it should be the basic ones and i can add
  // from the custom view in saas admin for the facility, also it maps from the pioneer envelope".
  const rxSurface = isFac ? 'fac_rx_list' : 'provider_rx_list'
  useEffect(() => { api.getRxViewLayout(undefined, rxSurface).then(setListCfg).catch(layoutFailed('prescription columns')) }, [rxSurface])
  const cfgCard = useMemo(() => (((listCfg?.cards as any[] | undefined) ?? []).find(c => c.card_key === 'list_columns')), [listCfg])
  const drugRefVals = useDrugRefValues(items.map(i => i.id), ((cfgCard?.fields ?? []) as any[]).some((f: any) => f.owlrx_table === 'drug_reference'))
  const subHidden = useMemo(() => new Set((((cfgCard?.config as any)?.subtext_hidden ?? []) as string[])), [cfgCard])
  type RxColU = { key: string; label: string; render: (rx: api.ProviderRxItem) => React.ReactNode }
  const rxCols: RxColU[] = useMemo(() => {
    const units: RxColU[] = [
      { key: 'rx_number', label: 'Rx #', render: rx => (<><div style={mono}>{rx.rx_number ?? '—'}</div>{!subHidden.has('rx_number') && rx.refill_number > 0 && <div style={mutedSm}>refill {rx.refill_number}</div>}</>) },
      { key: 'patient_name', label: 'Patient', render: rx => (<><div style={{ fontWeight: 500 }}>{rx.patient_name}</div></>) },
      { key: 'patient_dob', label: 'DOB', render: rx => <>{rx.patient_dob ? formatDob(rx.patient_dob) : '—'}</> },
      // D-VIEWCOLS-3 fix: prescriber is a NATIVE column on the provider Rx DTO
      // (prescribers.display_name, joined server-side). It was missing from this registry AND from
      // PROVIDER_RX_CATALOG, so the only way to surface it was a PioneerRx-mapped custom column —
      // which reads the ingest extraction store and is blank on every Rx not re-ingested since the
      // mapping was created. That is why the list showed — while the Rx detail, which reads the
      // join, showed the real name.
      { key: 'prescriber_name', label: 'Prescriber', render: rx => <>{rx.prescriber_name || '—'}</> },
      { key: 'drug_name', label: 'Drug', render: rx => (<><div style={{ fontWeight: 500 }}>{rx.drug_name}{rx.is_controlled && <span style={ctrlBadge}>{rx.prescribed_dea_schedule ?? 'CTRL'}</span>}</div>{!subHidden.has('drug_name') && <div style={mutedSm}>{rx.drug_strength} · {rx.drug_form}</div>}</>) },
      { key: 'sig', label: 'Sig', render: rx => <div style={truncStyle} title={rx.sig || undefined}>{rx.sig}</div> },
      { key: 'quantity_authorized', label: 'Quantity', render: rx => <>{rx.quantity_authorized} {rx.quantity_unit}</> },
      { key: 'refill_number', label: 'Fill #', render: rx => <>{rx.refill_number}</> },
      { key: 'ship_status', label: 'Delivery', render: rx => <ShipStatusPill rxId={rx.id} status={(rx as any).ship_status} deliveredAt={(rx as any).ship_delivered_at} /> },
      // D-SOC-2 (S2): where the patient receives this medication. The portal answers "did my
      // patient get their medication?" — WHERE it goes is part of that answer, and a prescriber
      // otherwise has no way to see that their patient is infused at a centre rather than at home.
      // Sasha's ruling: shown only inside the portal's existing patient scope, which the prescriber
      // predicate already enforces, so this is no new access path.
      // A facility name shows under the type when there is one; Home and self-administered have
      // none by design (they resolve to the patient's own address, SOC-3).
      { key: 'site_of_care', label: 'Site of care', render: rx => {
        const t = (rx as any).site_of_care_type as string | null | undefined
        const f = (rx as any).site_of_care_facility as string | null | undefined
        // Absent reads as absent. Never defaulted to "Home" — a fabricated clinical fact on a
        // prescriber-facing surface is worse than a blank (the §04 lesson from F-1).
        if (!t && !f) return <span style={mutedSm}>—</span>
        return (<>
          <div>{SITE_OF_CARE_LABELS[t ?? ''] ?? t}</div>
          {f && !subHidden.has('site_of_care') && <div style={mutedSm}>{f}</div>}
        </>)
      } },
      { key: 'directions_translated', label: 'Translated Directions', render: rx => <span style={{ fontSize: 12 }}>{(rx as any).directions_translated || '—'}</span> },
      { key: 'payer_state', label: 'Payer', render: rx => <PayerStatePill state={rx.payer_state} /> },
      { key: 'comments', label: 'Comments', render: rx => (rx.comment_count > 0 ? (<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MessageSquare size={12} aria-hidden="true" />{rx.comment_count}{rx.unread_for_provider > 0 && <span style={unreadDot} title={`${rx.unread_for_provider} unread`} />}</span>) : <span style={mutedSm}>—</span>) },
      // D-SOC-9 — units for every column the FACILITY catalog offers. Without an entry here a
      // configured column resolves to null in the mapping below and is silently dropped: the
      // builder showed 9 columns and the portal rendered 5, with no error anywhere. Mario hit
      // this repeatedly ("i see the facility-view has more fields in my prescrptions but i dont
      // see it in the portal"). A missing unit must never be a silent no-op — see the dev-time
      // warning at the end of this memo.
      { key: 'state', label: 'Rx status', render: rx => rx.state
          ? <span style={{ fontSize: 11.5, padding: '2px 8px', borderRadius: 20, background: '#f1f5f9' }}>{rx.state.replace(/_/g, ' ')}</span>
          : <span style={mutedSm}>—</span> },
      { key: 'drug_strength', label: 'Strength', render: rx => <>{rx.drug_strength || '—'}</> },
      { key: 'days_supply', label: 'Days supply', render: rx => <>{(rx as any).days_supply != null ? `${(rx as any).days_supply}d` : '—'}</> },
      { key: 'facility_name', label: 'Facility', render: rx => <>{(rx as any).site_of_care_facility || '—'}</> },
      { key: 'ship_tracking', label: 'Tracking', render: rx => <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{(rx as any).ship_tracking || '—'}</span> },
      { key: 'last_activity', label: 'Last activity', render: rx => <>{relativeTime(rx.last_state_change_at)}</> },
    ]
    // The portal answers "did my patient get their medication?" — DELIVERY is the status it
    // shows. The PMS statuses mirrored from PioneerRx, and the clinical state derived from them,
    // describe the pharmacy's internal workflow; they are dropped from this surface whatever the
    // saved column config asks for, and the Delivery column is guaranteed present.
    // D-SOC-2: 'site_of_care' joins the defaults next to Delivery — the two together answer
    // "did my patient get their medication, and where". A saved column config still wins.
    const DEFAULT_KEYS = ['rx_number', 'patient_name', 'drug_name', 'sig', 'quantity_authorized', 'ship_status', 'site_of_care', 'payer_state', 'comments', 'last_activity']
    // 'state' is dropped on the PRESCRIBER portal by design (the memo above: PMS/clinical state
    // describes the pharmacy's internal workflow, not "did my patient get their medication").
    // A FACILITY explicitly asked for it — a charge nurse needs to see on hold / out for delivery
    // / cancelled per resident — and it is in FAC_RX_CATALOG, so honour the config there.
    const PIONEER_COLS = new Set(isFac ? ['pioneerrx_rx_status', 'pioneerrx_tx_status']
                                       : ['state', 'pioneerrx_rx_status', 'pioneerrx_tx_status'])
    const byKey = new Map(units.map(u => [u.key, u]))
    // D-SOC-9: the catalogs name this column `site_of_care_type` (its real rx_site_of_care
    // column) while the renderer's key is `site_of_care`. Found by columnRendererParity.test.ts,
    // not by a bug report — enabling "Site of care" from the PRESCRIBER builder has been a silent
    // no-op. Alias rather than rename either side: the catalog name must match the DB column, and
    // renaming the unit key would orphan every saved config that already references it.
    const soc = byKey.get('site_of_care'); if (soc) byKey.set('site_of_care_type', soc)
    // Same class: the catalog offers 'site_of_care_facility' (the facility NAME) while the unit
    // that renders it as its own column is keyed 'facility_name'. Also found by the parity guard.
    const facn = byKey.get('facility_name'); if (facn) byKey.set('site_of_care_facility', facn)
    const cfgFields: any[] = cfgCard?.fields ?? []
    // D-SOC-9: a FACILITY with no saved layout must fall back to FACILITY defaults, not the
    // prescriber's. DEFAULT_KEYS is sig / payer_state / comments / rx_number — fields the
    // facility DTO does not return at all — so ASP (which has no fac_rx_list config) rendered a
    // table of blanks: empty Drug, "·" Sig, 0 Quantity. Mario saw exactly that in production.
    // These mirror FAC_RX_CATALOG's visible-by-default set.
    const FAC_DEFAULT_KEYS = ['rx_number', 'patient_name', 'drug_name', 'drug_strength', 'prescriber_name',
                              'quantity_authorized', 'days_supply', 'state', 'ship_status',
                              'facility_name', 'last_activity']
    if (cfgFields.length === 0)
      return (isFac ? FAC_DEFAULT_KEYS : DEFAULT_KEYS).map(k => byKey.get(k)).filter((u): u is RxColU => !!u)
    const out = cfgFields.filter(f => f.visible)
      .map(f => {
        if (f.owlrx_table === 'drug_reference') {
          return { key: `dr:${f.owlrx_column}`, label: (f.owlrx_label || '').trim() || f.owlrx_column,
                   render: (rx: api.ProviderRxItem) => f.owlrx_column === 'category'
                     ? <CategoryBadge name={drugRefVals[rx.id]?.category} color={drugRefVals[rx.id]?.category_color} />
                     : <span style={{ fontSize: 12 }}>{String((drugRefVals[rx.id] as any)?.[f.owlrx_column] ?? '—')}</span> } as RxColU
        }
        if (f.owlrx_table === 'custom') {
          // PioneerRx-mapped / custom column: value comes from the extraction store,
          // keyed list_columns.<column>. Blank until the rx's next envelope populates it.
          const vkey = `list_columns.${f.owlrx_column}`
          // A custom column whose key names a native unit falls back to that unit when the
          // extraction store has no value for the row, so an ALREADY-SAVED mapping (prescriber_name
          // added before it existed in the catalog) stops rendering blanks without an admin re-add.
          const native = byKey.get(f.owlrx_column)
          return { key: `cf:${f.owlrx_column}`, label: (f.owlrx_label || '').trim() || f.owlrx_column,
                   render: (rx: api.ProviderRxItem) => {
                     const v = cfv[rx.id]?.[vkey]
                     if (v !== null && v !== undefined && v !== '') return <span style={{ fontSize: 12 }}>{v}</span>
                     return native ? <>{native.render(rx)}</> : <span style={{ fontSize: 12 }}>—</span>
                   } } as RxColU
        }
        if (PIONEER_COLS.has(f.owlrx_column)) return null
        const u = byKey.get(f.owlrx_column)
        // A configured column with no unit used to return null and vanish without a trace — the
        // builder showed it, the portal did not, and nothing said why. Say so loudly in dev so the
        // next missing unit is found in seconds rather than over five rounds of bug reports.
        if (!u && import.meta.env.DEV)
          console.warn(`[view-builder] column "${f.owlrx_column}" is configured on this surface but has no renderer in rxCols — it will not appear. Add a unit for it.`)
        return u ? { ...u, label: (f.owlrx_label || '').trim() || u.label } : null
      })
      .filter((u): u is RxColU => !!u)
    if (!out.length) return (isFac ? FAC_DEFAULT_KEYS : DEFAULT_KEYS).map(k => byKey.get(k)).filter((u): u is RxColU => !!u)
    return out.some(u => u.key === 'ship_status') ? out : [...out, byKey.get('ship_status') as RxColU]
  }, [cfgCard, subHidden, cfv, drugRefVals, isFac])

  useEffect(() => { onScreenEnter() }, [onScreenEnter])

  async function reloadNotifications() {
    try {
      const n = await api.listNotifications(true)
      setUnreadCount(n.unread)
    } catch { /* best-effort */ }
  }

  useEffect(() => { reloadNotifications() }, [])

  return (
    <div style={pageStyle}>
      <header style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <h1 style={titleStyle}>{tab === 'patients' ? 'My patients' : tab === 'home' ? 'My practice' : 'My prescriptions'}</h1>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <button
              onClick={() => navigate('/notifications')}
              style={notifyBtn}
              aria-label={`${unreadCount} unread notifications`}
            >
              <Bell size={14} aria-hidden="true" />
              {unreadCount > 0 && <span style={notifyBadge}>{unreadCount}</span>}
              <span style={{ marginLeft: 4 }}>Inbox</span>
            </button>
          </div>
        </div>
        <div style={subtitleStyle}>
          {/* D-SOC-9: a nursing home did not WRITE these prescriptions — they belong to its
              residents. Same component, same tables; only the words change. */}
          {tab === 'patients'
            ? (isFac
                ? `Signed in as ${me?.display_name ?? ''}. Residents of your facilities.`
                : `Signed in as ${me?.display_name ?? ''}. Patients from prescriptions you've written.`)
            : tab === 'home'
              ? (isFac
                  ? `Signed in as ${me?.display_name ?? ''}. Your residents' prescriptions at a glance.`
                  : `Signed in as ${me?.display_name ?? ''}. Your prescribing at a glance.`)
              : (isFac
                  ? `Signed in as ${me?.display_name ?? ''}. ${summary ? summary.total_rx.toLocaleString() : '…'} prescriptions for residents of your facilities.`
                  : `Signed in as ${me?.display_name ?? ''}. ${summary ? summary.total_rx.toLocaleString() : '…'} prescriptions you've written.`)}
        </div>
      </header>

      {/* D-POT-8: documents this physician signs inside OwlRx (the Plan of Treatment). Providers only — not a
          provider nurse or a facility. Hidden when nothing is waiting. */}
      {!isFac && role === 'provider' && <PendingSignaturesCard />}

      {tab === 'home' ? (
        <ProviderHome summary={summary} recent={items} loading={loading}
          unread={unreadCount} navigate={navigate} portal={portal} sections={homeSections} />
      ) : tab === 'patients' ? (
        <div style={cardStyle}>
          {/* Always rendered, never gated on rows being present: with the search now applied on
              the server, a query that matches nothing returns an empty page — and hiding the box
              in that state leaves no way to correct the search that emptied it. */}
          <div style={{ ...searchWrap, marginBottom: 12 }}>
            <Search size={14} aria-hidden="true" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-mute)' }} />
            <input type="text" placeholder="Search patients by name, DOB or phone…"
              value={patientQ} onChange={e => setPatientQ(e.target.value)} style={searchInput} aria-label="Search patients" />
          </div>
          {/* With a header funnel applied the TABLE stays mounted, loading or empty. Replacing it
              with the loading or empty state unmounted the header: the funnel popover closed on
              every keystroke of a date bound, and a filter that matched nobody left no header to
              clear it from (lab + ASP walk-through, 2026-09-28). */}
          {patientsLoading && patServerFilters.length === 0 ? <LoadingState label="Loading your patients…" /> :
           patients.length === 0 && patServerFilters.length === 0 ? <EmptyState message={serverPatientQ
             ? `No patients match "${patientQ}".`
             : "No patients found for your prescriptions yet."} /> :
          <><Pager {...patientsPaged.pagerProps} label="patients" />
          <ListTable<api.ProviderPatientItem>
            viewKey="prov:patients"
            columns={patientCols}
            rows={patients}
            getRowKey={p => p.id}
            // D-SOC-9: a facility has NO patient-detail route. /provider/patients/:id is
            // RequireRole provider|provider_nurse (#2713 took facility off it), so clicking a row
            // rendered "That record could not be found" in production. Not clickable until a
            // facility patient detail exists.
            onRowOpen={isFac ? undefined : p => navigate(`/provider/patients/${p.id}`)}
            rowTitle={isFac ? undefined : "Double-click to open this patient"}
            emptyText={patServerFilters.length > 0
              ? `No ${isFac ? 'residents' : 'patients'} match these filters. Change or clear a filter in the column header.`
              : isFac ? "No residents found for your facilities yet." : "No patients found for your prescriptions yet."}
            loading={patientsLoading}
            skeletonRows={Math.min(patientsPaged.pagerProps.pageSize ?? 10, 10)}
            // Without caps AND onFilters, ListTable renders no funnel at all — which is why
            // /facility/residents showed a bare header (D-SOC-10), and why the provider's My
            // patients had none until D-UI-2. Both portals pass them now.
            caps={patCaps}
            filters={patColFilters}
            onFilters={(next: ColumnFilters) => { setPatColFilters(next); patientsPaged.pagerProps.onPage(1) }}
            // Only Facility lists its values, and only the facility portal has that column. Every
            // provider column is a text box or a range, which asks the server for no list.
            fetchOptions={isFac
              ? (key: string, term: string) => getFacilityRxFacet({ column: patServerKey(key), q: term })
              : undefined}
            sortKey={patSortKey}
            sortDir={patSortDir}
            onSort={(key: string) => {
              if (!patCaps[key]?.sortable) return
              setPatSortDir(d => (patSortKey === key && d === 'desc' ? 'asc' : 'desc'))
              setPatSortKey(key)
              patientsPaged.pagerProps.onPage(1)
            }}
          />
          <Pager {...patientsPaged.pagerProps} showPageSize={false} label="patients" /></>}
        </div>
      ) : (
      <>
      {/* PA action bar. The count comes from the SERVER summary, not from the loaded rows: it is a
          practice-wide statement ("N prescriptions need prior authorization"), and counting one
          page would understate it by however much the page happens to exclude. */}
      {(summary?.needs_attention ?? 0) > 0 && (
        <div style={paAlertStyle}>
          <AlertCircle size={16} aria-hidden="true" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, fontSize: 13 }}>
            <strong>{summary!.needs_attention.toLocaleString()}</strong>{' '}
            {summary!.needs_attention === 1 ? 'prescription needs' : 'prescriptions need'} attention.
          </div>
          <button onClick={() => setShipFilter('')} style={paJumpBtn}>
            View all
          </button>
        </div>
      )}

      {/* Filters */}
      <div style={filterRow}>
        <div style={searchWrap}>
          <Search size={14} aria-hidden="true" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-mute)' }} />
          <input
            type="text" placeholder="Search by drug, patient, DOB, Rx #…"
            value={q} onChange={e => setQ(e.target.value)}
            style={searchInput}
          />
        </div>
        <div style={chipsRow}>
          {SHIP_FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setShipFilter(f.value)}
              style={{
                ...chipStyle,
                ...(shipFilter === f.value ? chipActiveStyle : null),
              }}
            >{f.label}</button>
          ))}
        </div>
        {/* D-SOC-6: site-of-care chips, from the directory so every active type is filterable.
            The chip prefers the column's short label (the cell shows the same words) and falls
            back to the directory's own label for a type the map has never heard of. */}
        {!isFac && <div style={chipsRow}>
          {[{ value: '', label: 'All sites' },
            ...(socTypes.length
              ? socTypes.map(t => ({ value: t.code, label: SITE_OF_CARE_LABELS[t.code] ?? t.label }))
              : Object.entries(SITE_OF_CARE_LABELS).map(([value, label]) => ({ value, label })))]
            .map(f => (
              <button
                key={f.value || 'all-soc'}
                onClick={() => setSocFilter(f.value)}
                style={{
                  ...chipStyle,
                  ...(socFilter === f.value ? chipActiveStyle : null),
                }}
              >{f.label}</button>
            ))}
        </div>}
      </div>

      {layoutIssue && <ErrorBanner message={layoutIssue} onClose={() => setLayoutIssue(null)} />}
      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      <div style={cardStyle}>
        {/* The truncation banner is gone with the cap that produced it. The pager now states the
            real total, so there is no hidden remainder left to warn about. */}
        {/* With a header funnel applied the TABLE stays mounted, loading or empty: replacing it
            unmounts the header, which closes the popover on every keystroke of a bound and leaves
            a filter that matches nothing with no header to clear it from. */}
        {loading && serverFilters.length === 0 ? <LoadingState label="Loading your prescriptions…" /> :
         items.length === 0 && serverFilters.length === 0 ? <EmptyState message={(serverQ || shipFilter || socFilter)
            ? 'No prescriptions match the current search or filter.'
            : isFac
              ? 'No prescriptions for residents of your facilities yet.'
              : "You haven't written any prescriptions yet."} /> :
        <><Pager {...rxPaged.pagerProps} label="prescriptions" />
        {blockedCols.length > 0 && (
          // Honest degradation: a filter the server cannot push down is REPORTED, never quietly
          // dropped and never replayed over the page on screen (that would filter a sample and
          // present it as the whole caseload).
          <div style={{ fontSize: 12, color: 'var(--color-text-mute)', margin: '4px 0' }}>
            Not filterable on this list: {blockedCols.join(', ')}
          </div>
        )}
        <ListTable<api.ProviderRxItem>
          viewKey="prov:rx"
          columns={rxCols}
          rows={items}
          getRowKey={rx => rx.id}
          caps={effectiveCaps}
          filters={colFilters}
          onFilters={next => { setColFilters(next); rxPaged.pagerProps.onPage(1) }}
          loading={loading}
          fetchOptions={(column, term) => DS.facet({
            column, q: term,
            // Spreadsheet semantics: a column's own popover keeps its full option list while that
            // column is filtered; every OTHER active filter narrows it, so an option that would
            // return nothing is never offered.
            filters: serverFilters.filter(f => f.key !== column),
          }).then((r: any) => ({
            ...r,
            options: (r?.options ?? []).map((o: { value: string; label: string }) =>
              ({ ...o, label: funnelWords(column, o.value) ?? o.label })),
          }))}
          labelFor={(key, value) => labelForFilter(key, value) ?? funnelWords(key, value)}
          onLabels={rememberLabels}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={key => {
            if (capsAll[key]?.sortable === false) return
            setSortDir(d => (sortKey === key ? (d === 'asc' ? 'desc' : 'asc') : 'asc'))
            setSortKey(key)
            rxPaged.pagerProps.onPage(1)
          }}
          onRowOpen={rx => navigate(`/rx/${rx.id}`)}
          rowTitle="Double-click to open this prescription"
          emptyText="No prescriptions match these filters. Change or clear a filter in the column header."
          skeletonRows={Math.min(rxPaged.pagerProps.pageSize ?? 10, 10)}
        />
        <Pager {...rxPaged.pagerProps} showPageSize={false} label="prescriptions" /></>}
      </div>
      </>
      )}
    </div>
  )
}

const pageStyle: React.CSSProperties = { maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', padding: '24px 24px 48px' }
// Partial-set notice (same visual language as the StationQueue truncated banner).
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 28, fontWeight: 700, margin: 0, letterSpacing: -0.5,
}
const subtitleStyle: React.CSSProperties = { marginTop: 4, color: 'var(--color-text-soft)', fontSize: 13 }
const paAlertStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af',
  padding: '12px 16px', borderRadius: 6, marginBottom: 16,
}
const paJumpBtn: React.CSSProperties = {
  background: '#2563eb', color: '#fff', border: 'none',
  padding: '6px 12px', borderRadius: 4, fontSize: 12, fontWeight: 500,
  cursor: 'pointer', fontFamily: 'inherit',
}
const filterRow: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }
const searchWrap: React.CSSProperties = { position: 'relative', maxWidth: 400 }
const searchInput: React.CSSProperties = {
  width: '100%', padding: '8px 12px 8px 32px', fontSize: 13,
  border: '1px solid var(--color-border)', borderRadius: 6,
  fontFamily: 'inherit', boxSizing: 'border-box',
}
const chipsRow: React.CSSProperties = { display: 'flex', gap: 6, flexWrap: 'wrap' }
const chipStyle: React.CSSProperties = {
  padding: '4px 10px', fontSize: 12,
  border: '1px solid var(--color-border)', borderRadius: 999,
  background: '#fff', color: 'var(--color-text-soft)',
  cursor: 'pointer', fontFamily: 'inherit',
}
const chipActiveStyle: React.CSSProperties = {
  background: 'var(--color-primary, #0d9488)', color: '#fff',
  borderColor: 'var(--color-primary, #0d9488)',
}
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)',
  border: '1px solid var(--color-border)',
  borderRadius: 8, overflowX: 'auto', WebkitOverflowScrolling: 'touch',
}
const mutedSm: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-mute)' }
const mono: React.CSSProperties = { fontFamily: 'monospace', fontSize: 12 }
const truncStyle: React.CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
const ctrlBadge: React.CSSProperties = {
  marginLeft: 6, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
  padding: '1px 6px', background: '#fef3c7', color: '#854d0e', borderRadius: 3,
}
const unreadDot: React.CSSProperties = {
  display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
  background: '#dc2626', marginLeft: 4,
}
const notifyBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 10px', fontSize: 12,
  border: '1px solid var(--color-border)', borderRadius: 6,
  background: '#fff', cursor: 'pointer', fontFamily: 'inherit',
  position: 'relative',
}
const notifyBadge: React.CSSProperties = {
  background: '#dc2626', color: '#fff', borderRadius: 10,
  padding: '0 6px', fontSize: 10, fontWeight: 700, marginLeft: 4,
}
